import {
    ChangeDetectionStrategy,
    Component,
    DOCUMENT,
    ElementRef,
    HostBinding,
    inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy
} from '@angular/core';
import type { Chart, ChartConfiguration, ChartDataset, ChartType } from 'chart.js/auto';
import { Icons } from '../../icon/constants/icons.constants';
import { IconComponent } from '../../icon/icon.component';
import { LogoLoadingComponent } from '../../logo-loading/logo-loading.component';
import { defaultsDeep } from '../constants/chart.constants';

// Dynamic, not static: `chart.js/auto` registers at import, so a static import would put
// chart.js in every consumer's initial bundle, chart or not.
let chartJs: Promise<typeof Chart> | undefined;

function loadChartJs(): Promise<typeof Chart> {
    // Clearing on failure: a cached rejection would blank every chart for the page's life.
    chartJs ??= (async () => {
        const [{ Chart }, { default: annotationPlugin }, { default: zoomPlugin }] =
            await Promise.all([
                import('chart.js/auto'),
                import('chartjs-plugin-annotation'),
                import('chartjs-plugin-zoom'),
                // @ts-expect-error no types published for this package
                import('chartjs-adapter-date-fns')
            ]);

        Chart.register(annotationPlugin, zoomPlugin);

        return Chart;
    })().catch((error: unknown) => {
        chartJs = undefined;
        throw error;
    });

    return chartJs;
}

@Component({
    selector: 'nw-base-chart',
    templateUrl: './base-chart.component.html',
    styleUrl: './base-chart.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LogoLoadingComponent, IconComponent]
})
export abstract class BaseChartComponent<TType extends ChartType, TDatasetOptions>
    implements OnDestroy, OnChanges
{
    @Input() labels!: string[];
    @Input() datasets!: Partial<
        Pick<
            ChartDataset<TType, (number | [number, number] | null)[]>,
            // @ts-expect-error ignore
            | 'label'
            | 'data'
            | 'backgroundColor'
            | 'hoverBackgroundColor'
            | 'borderColor'
            | 'hoverBorderColor'
            | 'borderWidth'
            | 'hoverBorderWidth'
            | 'borderDash'
            | 'fill'
            | 'tension'
            | 'pointRadius'
            | 'pointBackgroundColor'
            | 'pointBorderColor'
            | 'pointHoverRadius'
            | 'pointStyle'
            | 'pointHitRadius'
            | 'showLine'
            | 'order'
            | 'pointHoverBackgroundColor'
            | 'pointHoverBorderColor'
            | 'radius'
            | 'hitRadius'
            | 'hoverRadius'
            | 'type'
        >
    >[];
    @Input() configuration?: Partial<ChartConfiguration>;
    @Input() width: string = '100%';

    @HostBinding('style.width') get setWidth() {
        return this.width;
    }

    public abstract datasetDefaults(index: number): Partial<TDatasetOptions>;
    public abstract configDefaults(): Partial<ChartConfiguration>;
    public abstract icon: Icons.Type;

    static TEMPLATE: string = ``;

    protected get ctx(): HTMLCanvasElement {
        return Array.from(
            (this.element.nativeElement as HTMLElement).children
        )[0] as HTMLCanvasElement;
    }

    // Optional, not definite: assignment is a microtask behind the date adapter's import.
    public chart?: Chart;
    protected theme!: 'dark' | 'light';
    private destroyed: boolean = false;
    private themeObserver?: MutationObserver;
    private resizeObserver?: ResizeObserver;
    private observedWidth?: number;
    private renderRequest: number = 0;

    private element: ElementRef = inject(ElementRef);
    private zone: NgZone = inject(NgZone);
    private document: Document = inject(DOCUMENT);

    constructor() {
        void loadChartJs();

        const HTML_ELEMENT: HTMLElement | undefined = Array.from(
            this.document.getElementsByTagName('html')
        )[0];

        this.theme = HTML_ELEMENT?.classList.contains('dark') ? 'dark' : 'light';

        this.themeObserver = this.classObserver(
            HTML_ELEMENT,
            'dark',
            (added: boolean) => {
                this.theme = added ? 'dark' : 'light';
                this.render();
            }
        );
    }

    public ngOnChanges(): void {
        this.render();
    }

    public ngOnDestroy(): void {
        this.destroyed = true;

        // <html> outlives this component, so an undisconnected observer stays live forever.
        this.themeObserver?.disconnect();
        this.themeObserver = undefined;

        this.resizeObserver?.disconnect();
        this.resizeObserver = undefined;

        this.destroy();
    }

    // Construction waits on chart.js's dynamic import, date adapter included: a time scale
    // built before it registers throws. Only the latest request may build — Chart.js rejects
    // a second chart on the same canvas.
    protected render(): void {
        this.destroy();

        const REQUEST: number = ++this.renderRequest;

        if (this.isLoading || !this.hasData) return;

        void loadChartJs().then((ChartJs) => {
            if (REQUEST !== this.renderRequest) return;
            if (this.destroyed || this.isLoading || !this.hasData) return;

            this.zone.runOutsideAngular(
                () => (this.chart = new ChartJs(this.ctx, this.config) as Chart)
            );
        });

        this.observeHostWidth();
    }

    // Not `options.onResize`: calling `resize()` from there re-enters Chart.js's resize path
    // and never terminates at a fractional width. Hence whole-pixel widths only.
    private observeHostWidth(): void {
        if (this.resizeObserver) return;

        const HOST: HTMLElement = this.element.nativeElement as HTMLElement;

        this.zone.runOutsideAngular(() => {
            this.observedWidth = Math.floor(HOST.getBoundingClientRect().width);

            this.resizeObserver = new ResizeObserver(() => {
                const WIDTH: number = Math.floor(HOST.getBoundingClientRect().width);

                if (WIDTH <= 0 || WIDTH === this.observedWidth) return;

                this.observedWidth = WIDTH;
                this.chart?.resize();
            });

            this.resizeObserver.observe(HOST);
        });
    }

    protected destroy(): void {
        // Cleared because the ResizeObserver outlives it, and Chart.js nulls its canvas.
        this.chart?.destroy();
        this.chart = undefined;
    }

    protected get config(): ChartConfiguration<TType> {
        return {
            ...defaultsDeep(
                (this.configuration as Partial<ChartConfiguration<TType>>) || {
                    options: {}
                },
                defaultsDeep(
                    {
                        options: {
                            responsive: true,
                            // No `onResize` — see `observeHostWidth`.
                            plugins: {
                                legend: {
                                    display: false
                                },
                                title: {
                                    display: false
                                }
                            }
                        }
                    },
                    this.configDefaults()
                )
            ),
            data: {
                labels: this.labels,
                datasets: this.datasets?.map(
                    (
                        {
                            label,
                            data,
                            backgroundColor,
                            hoverBackgroundColor,
                            borderColor,
                            hoverBorderColor,
                            borderWidth,
                            hoverBorderWidth,
                            borderDash,
                            tension,
                            fill,
                            pointRadius,
                            pointBackgroundColor,
                            pointBorderColor,
                            pointHoverRadius,
                            pointStyle,
                            pointHitRadius,
                            showLine,
                            order,
                            pointHoverBackgroundColor,
                            pointHoverBorderColor,
                            radius,
                            hitRadius,
                            hoverRadius,
                            type
                        },
                        index
                    ) => {
                        const DEFAULT_DATASETS: Partial<TDatasetOptions> =
                            this.datasetDefaults(index);

                        return {
                            ...DEFAULT_DATASETS,
                            ...Object.fromEntries(
                                Object.entries({
                                    backgroundColor,
                                    hoverBackgroundColor,
                                    borderColor,
                                    hoverBorderColor,
                                    borderWidth,
                                    hoverBorderWidth,
                                    borderDash,
                                    tension,
                                    fill,
                                    pointRadius,
                                    pointBackgroundColor,
                                    pointBorderColor,
                                    pointHoverRadius,
                                    pointStyle,
                                    pointHitRadius,
                                    showLine,
                                    order,
                                    pointHoverBackgroundColor,
                                    pointHoverBorderColor,
                                    radius,
                                    hitRadius,
                                    hoverRadius,
                                    type
                                }).filter(([, v]) => v !== undefined)
                            ),
                            label,
                            data
                        };
                    }
                )
            }
        } as unknown as ChartConfiguration<TType>;
    }

    private classObserver(
        targetElement: HTMLElement | undefined,
        observableClassName: string,
        callback: Function
    ): MutationObserver | undefined {
        if (!targetElement) return;

        const REFERENCE: {
            prevStateHasClass: boolean;
        } = {
            prevStateHasClass: targetElement.classList.contains(observableClassName)
        };
        const OBSERVER: MutationObserver = new MutationObserver(function (
            mutations,
            observer
        ) {
            // Use traditional 'for loops' for IE 11
            for (const MUTATION of mutations) {
                if (
                    MUTATION.type === 'attributes' &&
                    MUTATION.attributeName === 'class'
                ) {
                    const currentStateHasClass = (
                        MUTATION.target as HTMLElement
                    ).classList.contains(observableClassName);
                    if (REFERENCE.prevStateHasClass !== currentStateHasClass) {
                        REFERENCE.prevStateHasClass = currentStateHasClass;
                        const classAdded = currentStateHasClass;
                        callback.call(classAdded, classAdded, MUTATION, observer);
                    }
                }
            }
        });

        OBSERVER.observe(targetElement, { attributes: true });

        return OBSERVER;
    }

    protected get hasData(): boolean {
        return !!this.datasets?.length && !!this.labels?.length;
    }

    protected get isLoading(): boolean {
        return this.datasets === undefined || this.labels === undefined;
    }
}
